import * as React from "react"
import { useStore } from "zustand"
import { createStore } from "zustand/vanilla"

import { AnimatedListItem } from "@/components/ui/animated-list"
import { BlurFade } from "@/components/ui/blur-fade"
import { Button } from "@/components/button"
import { cn } from "@/lib/utils"
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Info,
  X,
} from "lucide-react"

/* -----------------------------------------------------------------------------
 * Types
 * -------------------------------------------------------------------------- */

export type ToastTone = "success" | "error" | "warning" | "info"

export interface Toast {
  id: string
  tone: ToastTone
  title?: string
  message: string
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

type ToastInput = Omit<Toast, "id">

/* -----------------------------------------------------------------------------
 * Toast Store (zustand/vanilla — no context, no provider)
 * -------------------------------------------------------------------------- */

interface ToastState {
  toasts: Toast[]
  addToast: (toast: ToastInput) => string
  dismissToast: (id: string) => void
  dismissAll: () => void
  success: (message: string, options?: Partial<ToastInput>) => string
  error: (message: string, options?: Partial<ToastInput>) => string
  warning: (message: string, options?: Partial<ToastInput>) => string
  info: (message: string, options?: Partial<ToastInput>) => string
}

const DEFAULT_DURATION = 5000

let toastCounter = 0

const timers = new Map<string, ReturnType<typeof setTimeout>>()

export const toastStore = createStore<ToastState>((set, _get) => {
  const dismissToast = (id: string) => {
    const timer = timers.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.delete(id)
    }
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
  }

  const addToast = (input: ToastInput): string => {
    const id = `toast-${++toastCounter}`
    const duration = input.duration ?? DEFAULT_DURATION

    const toast: Toast = { ...input, id }

    set((s) => ({ toasts: [...s.toasts, toast] }))

    if (duration > 0) {
      const timer = setTimeout(() => dismissToast(id), duration)
      timers.set(id, timer)
    }

    return id
  }

  return {
    toasts: [],

    addToast,

    dismissToast,

    dismissAll: () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
      set({ toasts: [] })
    },

    success: (message, options) =>
      addToast({ tone: "success", message, ...options }),

    error: (message, options) =>
      addToast({ tone: "error", message, duration: 8000, ...options }),

    warning: (message, options) =>
      addToast({ tone: "warning", message, ...options }),

    info: (message, options) =>
      addToast({ tone: "info", message, ...options }),
  }
})

/* -----------------------------------------------------------------------------
 * Convenience methods (no hook, no subscription, no rerender)
 * -------------------------------------------------------------------------- */

export const toast = {
  success: (message: string, options?: Partial<ToastInput>) =>
    toastStore.getState().success(message, options),
  error: (message: string, options?: Partial<ToastInput>) =>
    toastStore.getState().error(message, options),
  warning: (message: string, options?: Partial<ToastInput>) =>
    toastStore.getState().warning(message, options),
  info: (message: string, options?: Partial<ToastInput>) =>
    toastStore.getState().info(message, options),
  dismiss: (id: string) => toastStore.getState().dismissToast(id),
  dismissAll: () => toastStore.getState().dismissAll(),
}

/* -----------------------------------------------------------------------------
 * Data hook (only subscriber — the Toaster)
 * -------------------------------------------------------------------------- */

export function useToasts() {
  return useStore(toastStore, (s) => s.toasts)
}

/* -----------------------------------------------------------------------------
 * Toast Appearance Config
 * -------------------------------------------------------------------------- */

const toneConfig: Record<
  ToastTone,
  {
    icon: React.ElementType
    className: string
    defaultTitle: string
  }
> = {
  success: {
    icon: CheckCircle2,
    className:
      "border-success/30 bg-success/10 text-success dark:border-success/40 dark:bg-success/15",
    defaultTitle: "Success",
  },
  error: {
    icon: AlertCircle,
    className:
      "border-danger/30 bg-danger/10 text-danger dark:border-danger/40 dark:bg-danger/15",
    defaultTitle: "Error",
  },
  warning: {
    icon: AlertTriangle,
    className:
      "border-warning/30 bg-warning/10 text-warning-foreground dark:border-warning/40 dark:bg-warning/15",
    defaultTitle: "Warning",
  },
  info: {
    icon: Info,
    className:
      "border-info/30 bg-info/10 text-info dark:border-info/40 dark:bg-info/15",
    defaultTitle: "Info",
  },
}

/* -----------------------------------------------------------------------------
 * Toaster Component (the single toast-list subscriber)
 * -------------------------------------------------------------------------- */

export function Toaster() {
  const toasts = useToasts()

  if (toasts.length === 0) {
    return null
  }

  return (
    <div
      className="pointer-events-none fixed right-4 top-4 z-50 grid w-full max-w-sm gap-3"
      aria-live="polite"
      aria-relevant="additions text"
    >
      {toasts.map((toast, index) => {
        const config = toneConfig[toast.tone]
        const Icon = config.icon

        return (
          <BlurFade
            key={toast.id}
            delay={index * 0.02}
            duration={0.22}
            offset={-6}
            blur="4px"
          >
            <AnimatedListItem>
              <div
                role={toast.tone === "error" ? "alert" : "status"}
                aria-live={toast.tone === "error" ? "assertive" : "polite"}
                aria-atomic="true"
                className={cn(
                  "pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-lg backdrop-blur-md",
                  config.className
                )}
              >
                <Icon className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
                <div className="flex-1 space-y-1">
                  {toast.title && (
                    <div className="font-semibold">
                      {toast.title}
                    </div>
                  )}
                  <div className="text-sm opacity-90">{toast.message}</div>
                  {toast.action && (
                    <Button
                      variant="ghost"
                      size="xs"
                      className="mt-2 h-auto px-2 py-1"
                      onClick={() => {
                        toast.action?.onClick()
                        toastStore.getState().dismissToast(toast.id)
                      }}
                    >
                      {toast.action.label}
                    </Button>
                  )}
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-md p-1 opacity-60 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
                  onClick={() => toastStore.getState().dismissToast(toast.id)}
                  aria-label="Dismiss toast"
                >
                  <X className="size-4" />
                </button>
              </div>
            </AnimatedListItem>
          </BlurFade>
        )
      })}
    </div>
  )
}

export { toneConfig }
